import { apiClient } from '../api-client'
import {
  PaginatedResponse,
  SubtaskCreateRequest,
  SubtaskSummary,
  SubtaskUpdateRequest,
  Task,
  TaskFilters,
} from '@/types'
import { fileStorageApi } from './file-storage-api'
import { normalizeTaskStatus } from '../task-status'
import { getUserDisplayName } from '../user-display'

function normalizeTaskFilterParams(filters?: TaskFilters | Record<string, any>): Record<string, any> {
  const params: Record<string, any> = { ...(filters || {}) }

  if (params.projectId && !params.project_id) params.project_id = params.projectId
  if (params.milestoneId && !params.milestone_id) params.milestone_id = params.milestoneId
  if (params.assigneeId && !params.assigned_to) params.assigned_to = params.assigneeId
  if (params.dueDateFrom && !params.due_date_from) params.due_date_from = params.dueDateFrom
  if (params.dueDateTo && !params.due_date_to) params.due_date_to = params.dueDateTo

  delete params.projectId
  delete params.milestoneId
  delete params.assigneeId
  delete params.dueDateFrom
  delete params.dueDateTo

  return params
}

// Helper function to transform snake_case to camelCase
function transformTaskResponse(task: any): Task {
  const normalizedTaskId = task.id ?? task.task_id ?? task.taskId ?? task.task_uuid ?? task.uuid ?? task._id
  const normalizedAssignedTo = task.assigned_to ?? task.assignedTo ?? null
  const creator = task.creator
    ? {
        ...task.creator,
        name: getUserDisplayName(task.creator),
      }
    : undefined
  return {
    ...task,
    id: normalizedTaskId,
    status: normalizeTaskStatus(task.status, normalizedAssignedTo),
    projectId: task.project_id,
    milestoneId: task.milestone_id,
    assignedTo: normalizedAssignedTo,
    qaAssignedTo: task.qa_assigned_to,
    assigneeId: task.assigned_to, // For backward compatibility
    createdAt: task.created_at,
    updatedAt: task.updated_at,
    completedAt: task.completed_at,
    estimatedHours: task.estimated_hours,
    actualHours: task.actual_hours || 0,
    dueDate: task.due_date,
    parentTaskId: task.parent_task_id,
    timeSpent: task.actual_hours || 0, // For compatibility
    createdBy: task.created_by ?? task.createdBy,
    reporterId: task.created_by,
    reporter: creator,
    creator,
    subtasks: [],
    attachments: [],
    comments: [],
    subtasks_total: Number(task.subtasks_total || 0),
    subtasks_done: Number(task.subtasks_done || 0),
    subtasks_scope_total: Number(task.subtasks_scope_total || 0),
    subtasks_scope_done: Number(task.subtasks_scope_done || 0),
    subtasks_scope: task.subtasks_scope || 'self',
    // Transform assignee to include name field
    assignee: task.assignee ? {
      ...task.assignee,
      name: `${task.assignee.first_name} ${task.assignee.last_name}`.trim()
    } : undefined,
    qaAssignee: task.qa_assignee ? {
      ...task.qa_assignee,
      name: `${task.qa_assignee.first_name} ${task.qa_assignee.last_name}`.trim()
    } : undefined,
  }
}

function transformSubtaskResponse(item: any): SubtaskSummary {
  return {
    id: String(item.id || ''),
    parentTaskId: item.parent_task_id ?? item.parentTaskId ?? undefined,
    title: String(item.title || ''),
    description: item.description ?? undefined,
    assignedTo: item.assigned_to ?? item.assignedTo ?? undefined,
    assignee: item.assignee
      ? {
          ...item.assignee,
          name: `${item.assignee.first_name || ''} ${item.assignee.last_name || ''}`.trim(),
        }
      : undefined,
    dueDate: item.due_date ?? item.dueDate ?? undefined,
    sequence: Number(item.sequence || 0),
    status: normalizeTaskStatus(item.status, item.assigned_to) as Task['status'],
    estimatedHours: item.estimated_hours ?? item.estimatedHours ?? undefined,
    actualHours: item.actual_hours ?? item.actualHours ?? 0,
  }
}

function toSubtaskPayload(data: SubtaskCreateRequest | SubtaskUpdateRequest): Record<string, any> {
  return {
    title: data.title,
    description: data.description,
    assigned_to: data.assignedTo || undefined,
    due_date: data.dueDate || undefined,
    sequence: data.sequence,
    status: data.status,
    estimated_hours: data.estimatedHours,
  }
}

// Tasks API
export const tasksApi = {
  getTasks: async (filters?: TaskFilters, page = 1, limit = 20): Promise<PaginatedResponse<Task>> => {
    const response = await apiClient.get('/tasks', { params: { ...normalizeTaskFilterParams(filters), page, limit } })
    if (Array.isArray(response)) {
      return {
        data: response.map((task: any) => transformTaskResponse(task)),
        total: response.length,
        page,
        limit,
        totalPages: 1,
      }
    }

    const rows = (response?.data || []).map((task: any) => transformTaskResponse(task))
    return {
      data: rows,
      total: Number(response?.total || rows.length),
      page: Number(response?.page || page),
      limit: Number(response?.limit || limit),
      totalPages: Number(response?.totalPages || response?.total_pages || 1),
    }
  },

  getTasksByProject: async (projectId: string, filters?: TaskFilters): Promise<Task[]> => {
    const response = await apiClient.get('/tasks', {
      params: { ...normalizeTaskFilterParams(filters), project_id: projectId, page: 1, limit: 200 }
    })
    const tasks = Array.isArray(response) ? response : response?.data || []
    return tasks.map((task: any) => transformTaskResponse(task))
  },

  getTaskAssignees: (filters?: {
    project_id?: string
    milestone_id?: string
    status?: string
    priority?: string
    reporting_to_me?: boolean
    overdue?: boolean
    no_due_date?: boolean
    due_date_from?: string
    due_date_to?: string
    recently_created?: boolean
    assigned_this_week?: boolean
    completed_this_week?: boolean
    reopened?: boolean
    search?: string
  }): Promise<any[]> =>
    apiClient.get('/tasks/assignees', { params: filters || {} }),

  getTask: (id: string): Promise<Task> =>
    apiClient.get(`/tasks/${id}`).then(data => transformTaskResponse(data)),

  getTaskHistory: (id: string): Promise<any[]> =>
    apiClient.get(`/tasks/${id}/history`),

  createTask: (data: any): Promise<Task> =>
    apiClient.post('/tasks', data).then(response => transformTaskResponse(response)),

  updateTask: (id: string, data: any): Promise<Task> => {
    // Attachments are uploaded separately (S3 + complete-upload), not in task update payload.
    const { attachments, ...safeData } = data || {}
    return apiClient.put(`/tasks/${id}`, safeData).then(response => transformTaskResponse(response))
  },

  deleteTask: (id: string): Promise<void> =>
    apiClient.delete(`/tasks/${id}`),

  updateTaskStatus: (id: string, status: Task['status'], reason?: string): Promise<Task> =>
    apiClient.patch(`/tasks/${id}/status`, { status, reason }).then(response => transformTaskResponse(response)),

  reopenTask: (id: string, reason: string): Promise<Task> =>
    apiClient.post(`/tasks/${id}/reopen`, { reason }).then(response => transformTaskResponse(response)),

  pauseTask: (
    id: string,
    payload: { pause_type: 'client' | 'internal'; reason?: string; expected_resume_at?: string }
  ): Promise<Task> => apiClient.post(`/tasks/${id}/pause`, payload),

  resumeTask: (id: string): Promise<Task> =>
    apiClient.post(`/tasks/${id}/resume`).then(response => transformTaskResponse(response)),

  assignTask: (id: string, assigneeId: string): Promise<Task> =>
    apiClient.post(`/tasks/${id}/assign`, { assignee_id: assigneeId }).then(response => transformTaskResponse(response)),

  getTaskComments: (id: string): Promise<any[]> =>
    apiClient.get(`/tasks/${id}/comments`),

  addComment: (id: string, content: string, attachments?: File[], taggedUsers?: string[]): Promise<any> => {
    const formData = new FormData()
    formData.append('content', content)
    if (taggedUsers && taggedUsers.length > 0) {
      formData.append('tagged_users', JSON.stringify(taggedUsers))
    }
    if (attachments) {
      attachments.forEach(file => formData.append('attachments', file))
    }
    return apiClient.post(`/tasks/${id}/comments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  editComment: (taskId: string, commentId: string, content: string, taggedUsers?: string[]): Promise<any> => {
    const formData = new FormData()
    formData.append('content', content)
    if (taggedUsers && taggedUsers.length > 0) {
      formData.append('tagged_users', JSON.stringify(taggedUsers))
    }
    return apiClient.put(`/tasks/${taskId}/comments/${commentId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  addCommentAttachment: (id: string, commentId: string, file: File): Promise<any> =>
    apiClient.post(`/tasks/${id}/comments/${commentId}/attachments`, { file }, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  updateComment: (taskId: string, commentId: string, content: string): Promise<any> =>
    apiClient.put(`/tasks/${taskId}/comments/${commentId}`, { content }),

  deleteComment: (taskId: string, commentId: string): Promise<void> =>
    apiClient.delete(`/tasks/${taskId}/comments/${commentId}`),

  getKanbanTasks: (
    projectId: string,
    filter?: string,
    assignee?: string,
    pageSize?: number,
    reporting_to_me?: boolean,
    filters?: TaskFilters | Record<string, any>
  ): Promise<any> => {
    const normalizedFilters = normalizeTaskFilterParams(filters)
    const params: Record<string, any> = {
      ...normalizedFilters,
      pageSize,
      reporting_to_me,
    }
    if (projectId && projectId.trim()) params.projectId = projectId.trim()
    if (normalizedFilters.project_id && !params.projectId) params.projectId = normalizedFilters.project_id
    if (filter && filter.trim() && filter.trim().toLowerCase() !== 'all') params.filter = filter.trim()
    if (normalizedFilters.status && !params.filter) params.filter = normalizedFilters.status
    if (assignee && assignee.trim()) params.assignee = assignee.trim()
    if (normalizedFilters.assigned_to && !params.assignee) params.assignee = normalizedFilters.assigned_to
    delete params.project_id
    delete params.assigned_to
    delete params.status
    return apiClient.get('/tasks/kanban', { params })
  },

  addAttachment: async (id: string, file: File, projectId?: string): Promise<any> => {
    let resolvedProjectId = String(projectId || '').trim()
    if (!resolvedProjectId) {
      const task = await tasksApi.getTask(id)
      resolvedProjectId = String((task as any)?.projectId || (task as any)?.project_id || '').trim()
    }

    if (!resolvedProjectId) {
      throw new Error('Project ID is required to upload a task attachment')
    }

    const uploadPayload = {
      project_id: resolvedProjectId,
      category: 'tasks' as const,
      entity_type: 'task',
      entity_id: id,
    }

    return fileStorageApi.uploadEntityAttachment(uploadPayload, file)
  },

  removeAttachment: (taskId: string, attachmentId: string): Promise<void> =>
    apiClient.delete(`/tasks/${taskId}/attachments/${attachmentId}`),

  getSubtasks: (id: string): Promise<SubtaskSummary[]> =>
    apiClient.get(`/tasks/${id}/subtasks`).then((rows: any[]) =>
      Array.isArray(rows) ? rows.map((item) => transformSubtaskResponse(item)) : []
    ),

  getSubtask: (parentId: string, subtaskId: string): Promise<SubtaskSummary> =>
    apiClient.get(`/tasks/${parentId}/subtasks/${subtaskId}`).then((item: any) => transformSubtaskResponse(item)),

  createSubtask: (parentId: string, data: SubtaskCreateRequest): Promise<SubtaskSummary> =>
    apiClient
      .post(`/tasks/${parentId}/subtasks`, toSubtaskPayload(data))
      .then((item: any) => transformSubtaskResponse(item)),

  updateSubtask: (parentId: string, subtaskId: string, data: SubtaskUpdateRequest): Promise<SubtaskSummary> =>
    apiClient
      .put(`/tasks/${parentId}/subtasks/${subtaskId}`, toSubtaskPayload(data))
      .then((item: any) => transformSubtaskResponse(item)),

  deleteSubtask: (parentId: string, subtaskId: string): Promise<void> =>
    apiClient.delete(`/tasks/${parentId}/subtasks/${subtaskId}`),

  // Timer-related methods
  startTaskTimer: (taskId: string): Promise<any> =>
    apiClient.post(`/tasks/${taskId}/timer/start`),

  pauseTaskTimer: (taskId: string): Promise<any> =>
    apiClient.post(`/tasks/${taskId}/timer/pause`),

  stopTaskTimer: (taskId: string): Promise<any> =>
    apiClient.post(`/tasks/${taskId}/timer/stop`),

  getActiveTimer: (): Promise<any> =>
    apiClient.get('/tasks/timer/active'),

  getActiveTimers: (): Promise<any[]> =>
    apiClient.get('/tasks/timer/active/all'),

  getTaskWorkLogs: (taskId: string, params?: { user_id?: string; skip?: number; limit?: number }): Promise<any[]> =>
    apiClient.get(`/tasks/${taskId}/work-logs`, { params }),
}
